import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import MinimizeIcon from "@mui/icons-material/Minimize";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import axios from "axios";
import { useAuth } from "../../auth/useAuth";

import { APP_API_BASE } from "../../config/api";
const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || `${APP_API_BASE}`
).replace(/\/$/, "");
const API_V1_BASE_URL = `${API_BASE_URL}/api/v1`;
const AGENT_CHAT_URL = `${API_V1_BASE_URL}/agent/report-chat`;
const AGENT_API_KEY = process.env.REACT_APP_REPORT_AGENT_API_KEY || "";
const STORAGE_KEY = "report-copilot-guided-v1";

const REPORT_TYPES = ["alert_summary", "telemetry","audit", "device_health","device_log","firmware_compliance","inventory_status"];
const OUTPUT_FORMATS = ["csv", "excel", "pdf", "json"];
const FREQUENCIES = ["daily", "weekly", "monthly"];
const VALID_SEVERITIES = ["critical", "high", "warning", "info"];
const REPORT_TYPE_META = {
  alert_summary: { hasDevice: true, hasSeverity: true },
  telemetry: { hasDevice: true, hasSeverity: false },
};

const today = () => new Date().toISOString().slice(0, 10);

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

function newSessionId() {
  return `report-agent-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function parseNumber(value) {
  const n = Number.parseInt(String(value || "").trim(), 10);
  return Number.isFinite(n) ? n : null;
}

export default function ReportAgentChat() {
  const { activeOrg, user } = useAuth();
  const pollRef = useRef(null);
  const listRef = useRef(null);

  const token = localStorage.getItem("authToken");
  const orgId = activeOrg?.id || localStorage.getItem("activeOrgId");
  const actorUserId = user?.id || localStorage.getItem("user_id");
  const headers = useMemo(
    () => ({
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(orgId ? { "X-Org-ID": orgId } : {}),
    }),
    [token, orgId],
  );

  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [chatSession, setChatSession] = useState(newSessionId());
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Choose an action: Generate report or Schedules.",
    },
  ]);

  const [flow, setFlow] = useState({
    mode: "idle",
    step: "",
    data: {},
    ctx: {},
  });

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    try {
      const state = JSON.parse(raw);
      setOpen(Boolean(state.open));
      setMinimized(Boolean(state.minimized));
      if (Array.isArray(state.messages) && state.messages.length)
        setMessages(state.messages);
      if (state.chatSession) setChatSession(state.chatSession);
      if (state.flow?.mode === "history") {
        setFlow({ mode: "idle", step: "", data: {}, ctx: {} });
      } else if (state.flow) {
        setFlow(state.flow);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ open, minimized, messages, chatSession, flow }),
    );
  }, [open, minimized, messages, chatSession, flow]);

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);
  useEffect(() => {
    if (listRef.current)
      listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  const pushAssistant = (content, actions = []) =>
    setMessages((m) => [...m, { role: "assistant", content, actions }]);
  const pushUser = (content) =>
    setMessages((m) => [...m, { role: "user", content }]);

  const resetToMenu = (
    msg = "What do you want next? Generate report or Schedules.",
  ) => {
    setFlow({ mode: "idle", step: "", data: {}, ctx: {} });
    pushAssistant(msg);
  };

  const pollReport = (reportId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API_V1_BASE_URL}/reports/${reportId}`, {
          headers,
        });
        const report = res.data;
        if (["ready", "failed"].includes(report?.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (report.status === "ready") {
            pushAssistant(
              `Report is ready: ${report.report_type} (${report.output_format}).`,
              [
                {
                  type: "download_report",
                  reportId: report.id,
                  label: "Download report",
                },
              ],
            );
          } else {
            pushAssistant(
              `Report failed: ${report.error_message || "Unknown error"}`,
            );
          }
        }
      } catch {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 3000);
  };

  const openDownload = async (reportId) => {
    const res = await axios.get(`${API_V1_BASE_URL}/reports/${reportId}`, {
      headers,
    });
    const url = res.data?.download_url || res.data?.file_url;
    if (!url) throw new Error("No download URL available.");
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const fetchSchedules = async () => {
    const res = await axios.get(`${API_V1_BASE_URL}/reports/schedules`, {
      headers,
    });
    return Array.isArray(res.data) ? res.data : [];
  };

  const fetchDevices = async () => {
    const res = await axios.get(`${API_V1_BASE_URL}/devices`, { headers });
    const rows = Array.isArray(res.data) ? res.data : [];
    return rows
      .map((device) => ({
        id: String(device?.id || "").trim(),
        name: String(device?.name || "").trim(),
      }))
      .filter((device) => device.id);
  };

  const showSchedules = (rows) => {
    if (!rows.length) {
      pushAssistant(
        'No schedules found. Use "Create" below, or type "done" to exit.',
      );
      return;
    }
    const lines = rows.map((s, i) => {
      const scope = [];
      if (s?.parameters?.device_id) scope.push(`device:${s.parameters.device_id}`);
      if (s?.parameters?.severity) scope.push(`severity:${s.parameters.severity}`);
      const scopeText = scope.length ? ` [${scope.join(", ")}]` : "";
      return `${i + 1}. ${s.report_type}/${s.frequency}${scopeText} - ${s.enabled ? "On" : "Off"} - id:${s.id}`;
    });
    pushAssistant(
      `Schedules:\n${lines.join("\n")}\nUse quick-reply buttons below for actions.`,
    );
  };

  const runAgentFallback = async (question) => {
    const res = await axios.post(
      AGENT_CHAT_URL,
      {
        question,
        session_id: chatSession,
        org_id: orgId,
        ...(actorUserId ? { actor_user_id: actorUserId } : {}),
      },
      {
        headers: {
          ...headers,
          ...(AGENT_API_KEY ? { "Api-Key": AGENT_API_KEY } : {}),
          "Content-Type": "application/json",
        },
      },
    );
    const answer =
      res?.data?.data?.answer || res?.data?.message || "No response";
    pushAssistant(answer);
  };

  const startGenerateFlow = () => {
    setFlow({
      mode: "generate",
      step: "report_type",
      data: {
        report_type: "",
        output_format: "",
        date_from: daysAgo(30),
        date_to: today(),
        device_id: "",
        severity: "",
      },
      ctx: {},
    });
    pushAssistant(
      `Generate report step 1/6: choose report type (${REPORT_TYPES.join(", ")}).`,
    );
  };

  const startScheduleFlow = async () => {
    const [rows, devices] = await Promise.all([
      fetchSchedules(),
      fetchDevices().catch(() => []),
    ]);
    setDeviceOptions(devices);
    setFlow({
      mode: "schedules",
      step: "menu",
      data: {},
      ctx: { rows, devices },
    });
    showSchedules(rows);
  };

  const submitScheduleCreate = async (draft) => {
    const recipients = String(draft.recipients || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

    const meta = REPORT_TYPE_META[draft.report_type] || {
      hasDevice: false,
      hasSeverity: false,
    };

    const params = { date_window_days: draft.date_window_days };
    if (meta.hasDevice && draft.device_id) params.device_id = draft.device_id;
    if (meta.hasSeverity && draft.severity) params.severity = draft.severity;

    await axios.post(
      `${API_V1_BASE_URL}/reports/schedules/${orgId}`,
      {
        report_type: draft.report_type,
        frequency: draft.frequency,
        output_formats: draft.output_formats,
        recipients,
        parameters: params,
      },
      { headers },
    );

    pushAssistant("Schedule created.");
    const [rows, devices] = await Promise.all([
      fetchSchedules(),
      fetchDevices().catch(() => []),
    ]);
    setDeviceOptions(devices);
    setFlow({
      mode: "schedules",
      step: "menu",
      data: {},
      ctx: { rows, devices },
    });
    showSchedules(rows);
  };

  const handleGenerateStep = async (text) => {
    const value = text.trim().toLowerCase();
    const d = { ...flow.data };
    if (flow.step === "report_type") {
      if (!REPORT_TYPES.includes(value)) {
        pushAssistant(`Invalid report type. Use: ${REPORT_TYPES.join(", ")}`);
        return;
      }
      d.report_type = value;
      setFlow((f) => ({ ...f, step: "output_format", data: d }));
      pushAssistant(`Step 2/6: output format (${OUTPUT_FORMATS.join(", ")}).`);
      return;
    }
    if (flow.step === "output_format") {
      if (!OUTPUT_FORMATS.includes(value)) {
        pushAssistant(`Invalid format. Use: ${OUTPUT_FORMATS.join(", ")}`);
        return;
      }
      d.output_format = value;
      setFlow((f) => ({ ...f, step: "date_from", data: d }));
      pushAssistant(
        `Step 3/6: date_from (YYYY-MM-DD). Example: ${daysAgo(30)}`,
      );
      return;
    }
    if (flow.step === "date_from") {
      d.date_from = text.trim();
      setFlow((f) => ({ ...f, step: "date_to", data: d }));
      pushAssistant(`Step 4/6: date_to (YYYY-MM-DD). Example: ${today()}`);
      return;
    }
    if (flow.step === "date_to") {
      d.date_to = text.trim();
      setFlow((f) => ({ ...f, step: "device_id", data: d }));
      pushAssistant(`Step 5/6: device_id (optional). Type "-" to skip.`);
      return;
    }
    if (flow.step === "device_id") {
      d.device_id = text.trim() === "-" ? "" : text.trim();
      if (d.report_type === "alert_summary") {
        setFlow((f) => ({ ...f, step: "severity", data: d }));
        pushAssistant(
          `Step 6/6: severity (critical/high/warning/info) or "-" to skip.`,
        );
      } else {
        setFlow((f) => ({ ...f, step: "confirm", data: d }));
        pushAssistant(
          `Type "confirm" to generate ${d.report_type} in ${d.output_format}.`,
        );
      }
      return;
    }
    if (flow.step === "severity") {
      d.severity = text.trim() === "-" ? "" : value;
      setFlow((f) => ({ ...f, step: "confirm", data: d }));
      pushAssistant(
        `Type "confirm" to generate ${d.report_type} in ${d.output_format}.`,
      );
      return;
    }
    if (flow.step === "confirm") {
      if (value !== "confirm") {
        pushAssistant('Type "confirm" to run, or "cancel" to stop.');
        return;
      }
      const params = { date_from: d.date_from, date_to: d.date_to };
      if (d.device_id) params.device_id = d.device_id;
      if (d.severity) params.severity = d.severity;
      const res = await axios.post(
        `${API_V1_BASE_URL}/reports/generate/${orgId}`,
        {
          report_type: d.report_type,
          output_format: d.output_format,
          parameters: params,
        },
        { headers },
      );
      const report = res.data?.report;
      if (!report?.id) {
        pushAssistant("Report created but ID missing from response.");
        resetToMenu();
        return;
      }
      pushAssistant(
        `Started report generation. id=${report.id}. I will update once done.`,
      );
      pollReport(report.id);
      resetToMenu();
    }
  };

  const handleSchedulesStep = async (text) => {
    const value = text.trim().toLowerCase();
    if (flow.step === "menu") {
      if (value === "done") {
        resetToMenu();
        return;
      }
      if (value === "refresh") {
        const [rows, devices] = await Promise.all([
          fetchSchedules(),
          fetchDevices().catch(() => deviceOptions),
        ]);
        setDeviceOptions(devices);
        setFlow((f) => ({ ...f, ctx: { rows, devices } }));
        showSchedules(rows);
        return;
      }
      if (value === "create") {
        setFlow({
          mode: "schedules",
          step: "create_report_type",
          data: {
            report_type: "",
            frequency: "",
            output_formats: ["csv"],
            recipients: "",
            date_window_days: 7,
            device_id: "",
            severity: "",
          },
          ctx: flow.ctx,
        });
        pushAssistant(
          `Create schedule step 1: report_type (${REPORT_TYPES.join(", ")}).`,
        );
        return;
      }

      const [cmd, arg] = value.split(/\s+/);
      const index = parseNumber(arg);
      if (!["toggle", "delete"].includes(cmd) || !index) {
        pushAssistant(
          "Commands: create, toggle <n>, delete <n>, refresh, done",
        );
        return;
      }
      const row = (flow.ctx.rows || [])[index - 1];
      if (!row) {
        pushAssistant("Row number not found.");
        return;
      }
      if (cmd === "toggle") {
        await axios.patch(
          `${API_V1_BASE_URL}/reports/schedules/${row.id}`,
          { enabled: !row.enabled },
          { headers },
        );
        pushAssistant(`Toggled schedule ${row.id}.`);
      } else {
        await axios.delete(`${API_V1_BASE_URL}/reports/schedules/${row.id}`, {
          headers,
        });
        pushAssistant(`Deleted schedule ${row.id}.`);
      }
      const [rows, devices] = await Promise.all([
        fetchSchedules(),
        fetchDevices().catch(() => flow?.ctx?.devices || []),
      ]);
      setDeviceOptions(devices);
      setFlow((f) => ({ ...f, ctx: { rows, devices } }));
      showSchedules(rows);
      return;
    }

    const d = { ...flow.data };
    if (flow.step === "create_report_type") {
      if (!REPORT_TYPES.includes(value)) {
        pushAssistant(`Invalid report_type. Use: ${REPORT_TYPES.join(", ")}`);
        return;
      }
      d.report_type = value;
      setFlow((f) => ({ ...f, step: "create_frequency", data: d }));
      pushAssistant(`Step 2: frequency (${FREQUENCIES.join(", ")}).`);
      return;
    }
    if (flow.step === "create_frequency") {
      if (!FREQUENCIES.includes(value)) {
        pushAssistant(`Invalid frequency. Use: ${FREQUENCIES.join(", ")}`);
        return;
      }
      d.frequency = value;
      setFlow((f) => ({ ...f, step: "create_formats", data: d }));
      pushAssistant(
        `Step 3: output formats comma-separated (csv, excel, pdf). Example: csv,pdf`,
      );
      return;
    }
    if (flow.step === "create_formats") {
      const formats = value
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
      const allowed = ["csv", "excel", "pdf"];
      const invalid = formats.filter((x) => !allowed.includes(x));
      if (!formats.length || invalid.length) {
        pushAssistant("Invalid formats. Use any of: csv, excel, pdf");
        return;
      }
      d.output_formats = [...new Set(formats)];
      setFlow((f) => ({ ...f, step: "create_recipients", data: d }));
      pushAssistant("Step 4: recipients comma-separated emails.");
      return;
    }
    if (flow.step === "create_recipients") {
      const recipients = text
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
      if (!recipients.length) {
        pushAssistant("At least one recipient is required.");
        return;
      }
      d.recipients = recipients.join(",");
      setFlow((f) => ({ ...f, step: "create_window", data: d }));
      pushAssistant("Step 5: date_window_days (1, 7, 14, 30).");
      return;
    }
    if (flow.step === "create_window") {
      const n = parseNumber(value);
      if (![1, 7, 14, 30].includes(n)) {
        pushAssistant("Invalid value. Use one of: 1, 7, 14, 30");
        return;
      }
      d.date_window_days = n;
      const meta = REPORT_TYPE_META[d.report_type] || {
        hasDevice: false,
        hasSeverity: false,
      };
      if (meta.hasDevice) {
        setFlow((f) => ({ ...f, step: "create_device_id", data: d }));
        pushAssistant(
          `Next step: device_id (optional). Type "-" to use all devices.`,
        );
        return;
      }
      if (meta.hasSeverity) {
        setFlow((f) => ({ ...f, step: "create_severity", data: d }));
        pushAssistant(
          `Next step: severity (critical/high/warning/info) or "-" to skip.`,
        );
        return;
      }
      await submitScheduleCreate(d);
      return;
    }
    if (flow.step === "create_device_id") {
      const raw = text.trim();
      if (raw === "-" || !raw) {
        d.device_id = "";
      } else {
        const normalized = raw;
        const knownDevices = flow.ctx?.devices || deviceOptions;
        if (
          Array.isArray(knownDevices) &&
          knownDevices.length > 0 &&
          !knownDevices.some((device) => device.id === normalized)
        ) {
          pushAssistant(
            'Device not found in this organization. Use an exact device_id or "-" to skip.',
          );
          return;
        }
        d.device_id = normalized;
      }
      if (d.report_type === "alert_summary") {
        setFlow((f) => ({ ...f, step: "create_severity", data: d }));
        pushAssistant(
          `Final step: severity (critical/high/warning/info) or "-" to skip.`,
        );
        return;
      }
      await submitScheduleCreate(d);
      return;
    }
    if (flow.step === "create_severity") {
      const raw = text.trim().toLowerCase();
      if (raw === "-" || !raw) {
        d.severity = "";
      } else if (!VALID_SEVERITIES.includes(raw)) {
        pushAssistant(
          `Invalid severity. Use: ${VALID_SEVERITIES.join(", ")} or "-" to skip.`,
        );
        return;
      } else {
        d.severity = raw;
      }
      await submitScheduleCreate(d);
      return;
    }
  };

  const getQuickReplies = () => {
    if (busy) return [];

    if (flow.mode === "idle") {
      return [
        { label: "Generate report", value: "generate report" },
        { label: "Schedules", value: "schedules" },
      ];
    }

    if (flow.mode === "generate") {
      if (flow.step === "report_type")
        return REPORT_TYPES.map((x) => ({ label: x, value: x }));
      if (flow.step === "output_format")
        return OUTPUT_FORMATS.map((x) => ({ label: x, value: x }));
      if (flow.step === "date_from")
        return [
          { label: "Last 7 days", value: daysAgo(7) },
          { label: "Last 30 days", value: daysAgo(30) },
        ];
      if (flow.step === "date_to") return [{ label: "Today", value: today() }];
      if (flow.step === "device_id") return [{ label: "Skip", value: "-" }];
      if (flow.step === "severity") {
        return [
          { label: "critical", value: "critical" },
          { label: "high", value: "high" },
          { label: "warning", value: "warning" },
          { label: "info", value: "info" },
          { label: "Skip", value: "-" },
        ];
      }
      if (flow.step === "confirm")
        return [
          { label: "Confirm", value: "confirm" },
          { label: "Cancel", value: "cancel" },
        ];
    }

    if (flow.mode === "schedules") {
      if (flow.step === "menu") {
        const base = [
          { label: "Create", value: "create" },
          { label: "Refresh", value: "refresh" },
          { label: "Done", value: "done" },
        ];
        const rowActions = (flow.ctx.rows || []).slice(0, 3).flatMap((_, i) => [
          { label: `Toggle ${i + 1}`, value: `toggle ${i + 1}` },
          { label: `Delete ${i + 1}`, value: `delete ${i + 1}` },
        ]);
        return [...base, ...rowActions];
      }
      if (flow.step === "create_report_type")
        return REPORT_TYPES.map((x) => ({ label: x, value: x }));
      if (flow.step === "create_frequency")
        return FREQUENCIES.map((x) => ({ label: x, value: x }));
      if (flow.step === "create_formats") {
        return [
          { label: "csv", value: "csv" },
          { label: "excel", value: "excel" },
          { label: "pdf", value: "pdf" },
          { label: "csv,pdf", value: "csv,pdf" },
        ];
      }
      if (flow.step === "create_window") {
        return [
          { label: "1 day", value: "1" },
          { label: "7 days", value: "7" },
          { label: "14 days", value: "14" },
          { label: "30 days", value: "30" },
        ];
      }
      if (flow.step === "create_device_id") {
        const knownDevices = flow.ctx?.devices || deviceOptions;
        const options = Array.isArray(knownDevices) ? knownDevices : [];
        return [
          { label: "Skip", value: "-" },
          ...options.slice(0, 3).map((device) => ({
            label: device.name ? device.name : device.id.slice(0, 8),
            value: device.id,
          })),
        ];
      }
      if (flow.step === "create_severity") {
        return [
          { label: "critical", value: "critical" },
          { label: "high", value: "high" },
          { label: "warning", value: "warning" },
          { label: "info", value: "info" },
          { label: "Skip", value: "-" },
        ];
      }
    }

    return [{ label: "Cancel", value: "cancel" }];
  };

  const processText = async (rawText) => {
    const text = (rawText || "").trim();
    if (!text || busy) return;
    if (!token || !orgId) {
      setError("Missing auth or active organization.");
      return;
    }

    pushUser(text);
    setInput("");
    setError("");
    setBusy(true);

    try {
      const lower = text.toLowerCase();
      if (lower === "cancel") {
        resetToMenu("Cancelled. Choose Generate report or Schedules.");
        return;
      }
      if (lower.startsWith("download ")) {
        const id = text.split(/\s+/)[1];
        await openDownload(id);
        pushAssistant("Download opened.");
        return;
      }

      if (flow.mode === "idle") {
        if (lower.includes("generate")) {
          startGenerateFlow();
          return;
        }
        if (lower.includes("schedule")) {
          await startScheduleFlow();
          return;
        }
        await runAgentFallback(text);
        return;
      }

      if (flow.mode === "generate") {
        await handleGenerateStep(text);
        return;
      }
      if (flow.mode === "schedules") {
        await handleSchedulesStep(text);
      }
    } catch (e) {
      setError(
        e?.response?.data?.error ||
        e?.response?.data?.detail ||
        e.message ||
        "Request failed.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleMessage = async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    await processText(text);
  };

  const handleQuickReply = async (value) => {
    await processText(value);
  };

  const handleMessageAction = async (action) => {
    if (!action || busy) return;
    if (action.type !== "download_report" || !action.reportId) return;
    setBusy(true);
    setError("");
    try {
      await openDownload(action.reportId);
      pushAssistant("Download opened.");
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Download failed.");
    } finally {
      setBusy(false);
    }
  };

  const quickReplies = getQuickReplies();

  return (
    <Box sx={{ position: "fixed", right: 16, bottom: 16, zIndex: 1400 }}>
      {open && !minimized ? (
        <Paper
          elevation={8}
          sx={{
            position: { xs: "fixed", sm: "relative" },   // ✅ key fix

            width: { xs: "100vw", sm: 520 },             // ✅ full width mobile
            maxWidth: { xs: "100vw", sm: 520 },

            height: {
              xs: "100dvh",                              // ✅ full height mobile
              sm: "min(780px, calc(100dvh - 32px))",
            },

            bottom: { xs: 0, sm: "auto" },               // ✅ stick to bottom
            right: { xs: 0, sm: "auto" },

            borderRadius: { xs: 0, sm: 4 },              // ✅ remove curve mobile

            display: "grid",
            gridTemplateRows: "auto auto auto 1fr auto",
            overflow: "hidden",
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              px: 2,
              py: 1.2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#155E95",
              color: "white",
            }}
          >
            <Typography fontWeight={700}>Reports Guided Copilot</Typography>

            <Box>
              <IconButton
                size="small"
                sx={{ color: "white" }}
                onClick={() => {
                  setChatSession(newSessionId());
                  setFlow({ mode: "idle", step: "", data: {}, ctx: {} });
                  setMessages([
                    {
                      role: "assistant",
                      content:
                        "Session reset. Choose Generate report or Schedules.",
                    },
                  ]);
                }}
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                sx={{ color: "white" }}
                onClick={() => setMinimized(true)}
              >
                <MinimizeIcon fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                sx={{ color: "white" }}
                onClick={() => setOpen(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* TOP ACTIONS */}
          {flow.mode === "idle" ? (
            <Box
              sx={{
                px: 1.2,
                py: 1,
                display: "flex",
                gap: 0.7,
                flexWrap: "wrap",
                bgcolor: "#f1f5f9",
              }}
            >
              <Chip
                label="Generate report"
                onClick={() => handleQuickReply("generate report")}
              />
              <Chip
                label="Schedules"
                onClick={() => handleQuickReply("schedules")}
              />
            </Box>
          ) : (
            <Box />
          )}

          <Box />

          {/* ERROR */}
          {error ? (
            <Alert severity="error" sx={{ m: 1 }}>
              {error}
            </Alert>
          ) : (
            <Box />
          )}

          {/* CHAT */}
          <Stack
            ref={listRef}
            spacing={1}
            sx={{ p: 1.2, overflowY: "auto", bgcolor: "#f8fafc" }}
          >
            {messages.map((m, i) => (
              <Paper
                key={`${m.role}-${i}`}
                sx={{
                  p: 1,
                  bgcolor: m.role === "user" ? "#dbeafe" : "white",
                  alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                  maxWidth: "92%",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {m.role}
                </Typography>

                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {m.content}
                </Typography>

                {m.role === "assistant" &&
                  Array.isArray(m.actions) &&
                  m.actions.length ? (
                  <Box
                    sx={{
                      mt: 0.8,
                      display: "flex",
                      gap: 0.6,
                      flexWrap: "wrap",
                    }}
                  >
                    {m.actions.map((a, ai) => (
                      <Button
                        key={`${a.type}-${a.reportId || ai}`}
                        size="small"
                        variant="contained"
                        onClick={() => handleMessageAction(a)}
                        disabled={busy}
                      >
                        {a.label || "Action"}
                      </Button>
                    ))}
                  </Box>
                ) : null}
              </Paper>
            ))}

            {busy ? <CircularProgress size={18} /> : null}
          </Stack>

          {/* QUICK REPLIES */}
          <Box
            sx={{
              px: 1.2,
              py: 0.8,
              display: "flex",
              gap: 0.6,
              flexWrap: "wrap",
              bgcolor: "white",
              borderTop: "1px solid #eef2f7",
            }}
          >
            {quickReplies.map((q) => (
              <Chip
                key={`${flow.mode}-${flow.step}-${q.value}`}
                label={q.label}
                onClick={() => handleQuickReply(q.value)}
                disabled={busy}
                sx={{ height: 26 }}
              />
            ))}
          </Box>

          {/* INPUT */}
          <Box
            sx={{
              p: 1.2,
              display: "flex",
              gap: 1,
              bgcolor: "white",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              value={input}
              placeholder='Type response, or "cancel"'
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleMessage();
                }
              }}
            />

            <Fab
              size="medium"
              onClick={handleMessage}
              disabled={busy || !input.trim()}
            >
              <SendIcon />
            </Fab>
          </Box>
        </Paper>
      ) : open && minimized ? (
        <Paper
          sx={{
            px: 1.2,
            py: 0.8,
            borderRadius: 999,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }} 
        >
          <Typography
            sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#155E95" }}
          >
            Reports Copilot
          </Typography>

          <IconButton size="small" onClick={() => setMinimized(false)}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>

          <IconButton size="small" onClick={() => setOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      ) : (
        <Tooltip title="Open Reports Copilot">
          <Fab
            onClick={() => {
              setOpen(true);
              setMinimized(false);
            }}
            sx={{
              width: 68,
              height: 68,
              background: "#fff",
              border: "2px solid #0284c7",
            }}
          >
            <Typography
              sx={{ fontWeight: 800, color: "#155E95", fontSize: "0.76rem" }}
            >
              AI
            </Typography>
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
}
